/**
 * Determines the default route to redirect a user to after authentication,
 * based on their organization type and role.
 *
 * This utility centralizes redirect logic that was previously duplicated
 * across PublicRoutes, SigninCover/Form, and SignupCover/Form.
 *
 * @param {Object} options
 * @param {Object} [options.organization] - The organization object from the signin response or userInfo
 * @param {string} [options.organization.type] - Organization type (e.g., 'demo_org', 'w_org')
 * @param {string[]} [options.orgRoles] - User's organization roles (e.g., ['ORG_ADMIN'])
 * @param {string[]} [options.teamRoles] - User's team roles (e.g., ['TEAM_ADMIN'])
 * @param {string[]} [options.designations] - User's designations (e.g., ['account_admin'])
 * @returns {string} The route path to redirect to
 */
export const getDefaultRoute = ({
  organization,
  // eslint-disable-next-line
  orgRoles = [],
  // eslint-disable-next-line
  teamRoles = [],
  // eslint-disable-next-line
  designations = [],
} = {}) => {
  const orgType = organization?.type;

  // Whitelabel org admins go to customer settings
  if (orgType === 'w_org') {
    return '/customer/settings';
  }

  // Demo orgs go to conversation AI
  if (orgType === 'demo_org') {
    return '/conversation-ai';
  }

  // All other authenticated users go to deals
  return '/deals';
};

/**
 * Builds redirect params from a signin/signup response for use with getDefaultRoute.
 *
 * @param {Object} signinResponse - The response from the signin/signup API
 * @returns {Object} Params compatible with getDefaultRoute
 */
/**
 * Builds redirect params from a signin/signup response for use with getDefaultRoute.
 *
 * @param {Object} signinResponse - The response from the signin/signup API.
 * @returns {Object} Params compatible with getDefaultRoute.
 */
export const buildRedirectParams = (signinResponse) => ({
  organization: signinResponse?.organization,
  orgRoles: signinResponse?.user?.orgRoles || [],
  teamRoles: signinResponse?.user?.teamRoles || [],
  designations: signinResponse?.user?.designations || [],
});

/**
 * Builds redirect params from localStorage userInfo for use with getDefaultRoute.
 *
 * @param {Object} userInfo - Parsed userInfo from localStorage
 * @returns {Object} Params compatible with getDefaultRoute
 */
/**
 * Builds redirect params from localStorage userInfo for use with getDefaultRoute.
 *
 * @param {Object} userInfo - Parsed userInfo from localStorage.
 * @returns {Object} Params compatible with getDefaultRoute.
 */
export const buildRedirectParamsFromUserInfo = (userInfo) => ({
  organization: { type: userInfo?.custom?.orgType },
  orgRoles: userInfo?.orgRoles || [],
  teamRoles: userInfo?.teamRoles || [],
  designations: userInfo?.designations || [],
});
